import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useQuery } from "react-query";

import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
} from "@mui/x-data-grid";

import { apis, useZusParams } from "..";
import { PERMISSION_KEY, TRANSLATE_PREFIX } from "../../..";
import { CustomPagination } from "../../../../../../components/CustomPagination";
import LoadingDialog from "../../../../../../components/LoadingDialog";
import { NoRowsOverlay } from "../../../../../../components/NoRowsOverlay";
import useColumnHide, {
  ColumnHiddenMessage,
  getColumnHideConfig,
} from "../../../../../../hooks/useColumnHide";
import { selectCrossMerchant } from "../../../../../../reducer/crossMerchantSlice";
import { useAppSelector } from "../../../../../../reducer/hooks";
import { dataGridDefaults } from "../../../../../../utils/constant";
import { useGenGridCol } from "../../../../../../utils/HelperComp";
import useTabTranslation from "src/hooks/useTabTranslation";
import ViewDialog from "./ViewDialog";
import { GetAllRes, GroupSetting, ViewDetails } from "../types/GetAllRes";
import { useAlerting, usePermission } from "src/hooks";
import cleanParams from "../../../helpers/cleanParams";
import { TAB_TRANSLTE_PREFIX } from "../constants";
import OpIconButton from "src/components/OpIconButton";
import { OpRedirect, OpView } from "src/assets/icons";
import GroupSettingDialog from "./GroupSettingDialog";

export default function Table(props: {
  listMapping: (which: "key" | "name", array: any[], config?: any) => any[][];
  setCount: Dispatch<SetStateAction<number>>;
}) {
  const { alerting } = useAlerting();
  const { hasPermission } = usePermission();
  const [viewDetails, setViewDetails] = useState<ViewDetails[]>([]);
  const [startGroupSettings, setStartGroupSettings] = useState<GroupSetting>();
  const [endGroupSettings, setEndGroupSettings] = useState<GroupSetting>();
  const { t, translate } = useTabTranslation({
    featurePrefix: TRANSLATE_PREFIX,
    tabPrefix: TAB_TRANSLTE_PREFIX,
  });
  const zusParams = useZusParams();
  const { merchantMapping } = useAppSelector(selectCrossMerchant);
  const { listMapping, setCount } = props;
  const listRes = useQuery({
    queryKey: [TAB_TRANSLTE_PREFIX, zusParams.body, zusParams.refetchCounter],
    queryFn: () => apis.getAll(cleanParams(zusParams.body)),
  });
  const { rows = [], count = 0 } =
    (listRes.data as any as { rows: any[]; count: number }) || {};
  const content: GridRowsProp = listMapping("key", rows, {
    t,
    translate,
    merchantMapping,
  });

  useEffect(() => setCount(count), [listRes]);

  const columns: GridColDef<any, any, GetAllRes>[] = [
    useGenGridCol("operation", t("operation"), {
      renderCell: (
        params: GridRenderCellParams<any, { rawData: GetAllRes }>,
      ) => {
        const row = params.row.rawData;
        return (
          <OpIconButton
            isDisabled={!row?.asset_dest_report_balances?.length}
            title={t("view")}
            url={OpView}
            onClick={() => {
              setViewDetails(row?.asset_dest_report_balances || []);
            }}
          />
        );
      },
    }),
    useGenGridCol("groupName", t("groupName"), { minWidth: 150 }),
    useGenGridCol("groupType", t("groupType"), { minWidth: 150 }),
    useGenGridCol("groupSequence", t("groupSequence"), { minWidth: 150 }),
    useGenGridCol("chainName", t("chainName"), { minWidth: 150 }),
    useGenGridCol("currency", t("currency")),
    useGenGridCol("highRiskAmount", t("highRiskAmount"), { minWidth: 150 }),
    useGenGridCol("lowRiskAmount", t("lowRiskAmount"), { minWidth: 150 }),
    useGenGridCol("totalAmount", t("totalAmount"), { minWidth: 150 }),
    useGenGridCol("activeStartTime", t("activeStartTime"), { minWidth: 150 }),
    useGenGridCol("activeEndTime", t("activeEndTime"), {
      minWidth: 150,
    }),
    useGenGridCol("rotationReason", t("rotationReason"), {
      minWidth: 150,
    }),
    useGenGridCol("startGroupSetting", t("startGroupSetting"), {
      minWidth: 150,
      renderCell: (
        params: GridRenderCellParams<any, { rawData: GetAllRes }>,
      ) => {
        const row = params.row.rawData;
        return (
          <OpIconButton
            isDisabled={!row?.start_group_setting}
            title={t("startGroupSetting")}
            url={OpRedirect}
            onClick={() => {
              setStartGroupSettings(row?.start_group_setting);
            }}
            size="1.5rem"
          />
        );
      },
    }),
    useGenGridCol("endGroupSetting", t("endGroupSetting"), {
      minWidth: 150,
      renderCell: (
        params: GridRenderCellParams<any, { rawData: GetAllRes }>,
      ) => {
        const row = params.row.rawData;
        return (
          <OpIconButton
            isDisabled={!row?.end_group_setting}
            title={t("endGroupSetting")}
            url={OpRedirect}
            onClick={() => {
              setEndGroupSettings(row?.end_group_setting);
            }}
            size="1.5rem"
          />
        );
      },
    }),
  ].filter(
    ({ field }) =>
      field !== "operation" ||
      (field === "operation" &&
        hasPermission(PERMISSION_KEY.TabContractSweepRecipientGroup.View)),
  );

  useEffect(() => () => zusParams.clear(), []);
  const { colsShown, setColsShown, hasColHidden, localeText } = useColumnHide(
    columns,
    getColumnHideConfig(),
  );
  if (listRes.isLoading) return <LoadingDialog forceOpen={true} />;
  return (
    <>
      <ViewDialog
        isOpen={!!viewDetails.length}
        onClose={() => setViewDetails([])}
        viewDetails={viewDetails}
      />
      <GroupSettingDialog
        isOpen={!!startGroupSettings}
        onClose={() => setStartGroupSettings(undefined)}
        groupSetting={startGroupSettings}
        title={t("startGroupSetting")}
      />
      <GroupSettingDialog
        isOpen={!!endGroupSettings}
        onClose={() => setEndGroupSettings(undefined)}
        groupSetting={endGroupSettings}
        title={t("endGroupSetting")}
      />
      <ColumnHiddenMessage hasColHidden={hasColHidden} />
      <DataGrid
        {...dataGridDefaults}
        localeText={localeText}
        columnVisibilityModel={colsShown}
        onColumnVisibilityModelChange={setColsShown}
        rows={content}
        rowCount={count}
        columns={columns}
        page={zusParams.body.page}
        onPageChange={zusParams.setPage}
        components={{
          NoRowsOverlay,
          Footer: CustomPagination,
        }}
        componentsProps={{
          footer: { totalRecords: count },
        }}
      />
    </>
  );
}
